import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { t } from '../../../services/i18nService';
import styles from '../QuestionDisplay.scss';
import storeManager from '../../../store/StoreManager';
import itemFlagActions from '../../../actions/ItemFlagActions';
import a11yService from '../../../services/a11yService';
import itemFlagButtonSelector from '../../../selectors/question/itemFlags/ItemFlagButtonSelector';
import scorableItemReader from '../../../store/readHelpers/ScorableItemReader';

const ItemFlagButton = (props) => {
    const {
        flagId,
        isCurrentItem,
        isFlagged,
        isFocused,
        isHorizontal,
        orderNumber,
        questionRevisionId,
        shouldExcludeFlag,
        shouldHideFlag,
        stateCache,
    } = props;

    const [hideFlag, setHideFlag] = useState(!!orderNumber);
    const [offsetTop, setOffsetTop] = useState(0);
    const [offsetLeft, setOffsetLeft] = useState(0);

    useEffect(() => {
        setOffsetPosition();
        let resizeObserver;

        const shouldAddObserver = isCurrentItem && !hideFlag;
        if (shouldAddObserver) {
            // Create resize observer for the question element
            resizeObserver = new ResizeObserver(([entry]) => {
                if (entry.target.id === `question-${questionRevisionId}`) setOffsetPosition();
            });

            // Observe both window resize and question element resize
            const questionElement = document.getElementById(`question-${questionRevisionId}`);
            if (questionElement) resizeObserver.observe(questionElement);
            window.addEventListener('resize', setOffsetPosition);
        } else {
            window.removeEventListener('resize', setOffsetPosition);
        }

        return () => {
            if (resizeObserver) resizeObserver.disconnect();
            window.removeEventListener('resize', setOffsetPosition);
        };
    }, [isCurrentItem, hideFlag, shouldHideFlag, questionRevisionId]);

    useEffect(() => {
        if (shouldHideFlag) {
            setHideFlag(true);
        }
    }, [shouldHideFlag]);

    const setOffsetPosition = () => {
        if (!isCurrentItem) {
            return;
        }

        setTimeout(() => {
            const questionElement = document.getElementById(`question-${questionRevisionId}`);
            const itemElement = document.getElementById(`scorableItem-${questionRevisionId}_${orderNumber}`);
            if (questionElement && itemElement) {
                if (isHorizontal) {
                    const sectionElement = document.getElementById('sectionContent');
                    const wrapper = itemElement.closest('.contains-horizontal-flagging');
                    const left = (itemElement.getBoundingClientRect().left - sectionElement.getBoundingClientRect().left) + wrapper.scrollLeft;
                    setOffsetLeft(Math.round(left));
                } else {
                    setOffsetTop(Math.round(itemElement.getBoundingClientRect().top - questionElement.getBoundingClientRect().top));
                }
                setHideFlag(false);
            }
        }, 250);
    };

    const toggleFlag = () => {
        storeManager.dispatch(itemFlagActions.toggleItemFlag(flagId, !isFlagged));

        const { itemFlag } = storeManager.getState();
        stateCache.setFlaggedItems(itemFlag.flaggedItems);

        a11yService.focusElement(flagId);
    };

    const setFocusedFlag = () => {
        storeManager.dispatch(itemFlagActions.focusItemFlag(flagId));
    };

    const clearFocusedFlag = () => {
        storeManager.dispatch(itemFlagActions.focusItemFlag(null));
    };

    const composeStyle = () => {
        if (shouldHideFlag || hideFlag) {
            return null;
        }

        return isHorizontal
            ? { left: `${offsetLeft - 10}px` }
            : { top: `${offsetTop - 5}px` };
    };

    if (shouldExcludeFlag) {
        return null;
    }

    const tabIndex = scorableItemReader.useNumberPerInteraction() ? -1 : 0;
    const itemFlagClassName = isFlagged ? 'fa fa-bookmark' : 'fa fa-bookmark-o';
    const classList = [styles.txtButton];
    if (isFocused) {
        classList.push(styles.focusedFlag);
    }

    if (shouldHideFlag || hideFlag) {
        classList.push(styles.hiddenFlag);
    } else {
        classList.push(styles.visibleFlag);
    }

    return (
        <button
            onClick={toggleFlag}
            onFocus={setFocusedFlag}
            onBlur={clearFocusedFlag}
            aria-pressed={isFlagged}
            className={classList.join(' ')}
            style={composeStyle()}
            tabIndex={tabIndex}
        >
            <i className={itemFlagClassName} aria-hidden="true" />
            <span className="sr-only">
                { isFlagged
                    ? `${t('question.unflagQuestionNumber')} ${orderNumber}`
                    : `${t('question.flagQuestionNumber')} ${orderNumber}`
                }
            </span>
        </button>
    );
};

ItemFlagButton.propTypes = {
    flagId: PropTypes.string.isRequired,
    isCurrentItem: PropTypes.bool.isRequired,
    isFlagged: PropTypes.bool.isRequired,
    isFocused: PropTypes.bool.isRequired,
    isHorizontal: PropTypes.bool,
    orderNumber: PropTypes.number.isRequired,
    questionRevisionId: PropTypes.number.isRequired,
    shouldExcludeFlag: PropTypes.bool.isRequired,
    shouldHideFlag: PropTypes.bool.isRequired,
    stateCache: PropTypes.object.isRequired,
};

const mapStateToProps = (state, ownProps) => ({
    ...itemFlagButtonSelector.selectProps(state, ownProps),
});
export default connect(mapStateToProps)(ItemFlagButton);
